import _ from 'lodash';

/** Formats a duration in seconds as e.g. "3m 42s". */
export function secondsToMinString(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds - minutes * 60;
  return `${minutes}m ${remainingSeconds.toFixed(0)}s`;
}

/** Strips a single leading and trailing double quote. */
export function getRidOfQuotes(string: string): string {
  return string.replace(/(^"|"$)/g, '');
}

/** Removes all tab characters. */
export function getRidOfTabs(string: string): string {
  return string.replace(/\t/g, '');
}

/**
 * Recursively flattens an object into one level, where keys are separated by dot.
 * **WARNING**: Be wary not to use it on circular objects.
 * @param object nested object to flatten
 */
export function flattenObject(object: object): Record<string, any> {
  const flat: Record<string, any> = {};

  for (const [key,value] of Object.entries(object)) {
    // You can have keys with undefined (esp. since it's more efficient than `delete obj.key`) but we don't want the included
    if (value===undefined) continue;

    // If it's an object, flatten it recursively...
    if (_.isObject(value)) {
      const ref = flattenObject(value);
      for (const [k,v] of Object.entries(ref)) flat[`${key}.${k}`] = v;

      continue; // Guard clause
    }

    // ... else it's some value
    flat[key] = value;
  }

  return flat;
}

/** A named string entry, e.g. a quick-reply label: `{name: 'qr0', data: 'My Label'}`. */
export interface NamedString {
  /** Identifier referenced from templates, optionally wrapped in braces (`{qr0}`). */
  name: string;
  /** The display text associated with the name. */
  data: string;
}

/**
 * @param strings E.g. `[{name: 'qr0', data: 'My Label'}]`
 * @param name E.g. `{qr0}` (surrounding braces are stripped before lookup)
 * @returns Found object or null, e.g. `{name: 'qr0', data: 'My Label'}` or `null` if not found
 */
export function getStringsObj(strings: NamedString[], name: string | null | undefined): NamedString | null {
  if (!name) return null;

  if (name.startsWith("{") && name.endsWith("}")) name = name.slice(1, -1);
  return strings.find((s) => s.name === name) ?? null;
}

/** Context given to a {@link flattenObjectTransformer} transformer for each visited value. */
export interface TransformerOptions {
  /** The key that is associated with the current value */
  key: string;
  /** The path leading to the given key, if any */
  path: string | null;
}

/** Decision returned by a transformer about how to treat the visited value. */
export interface TransformerResult {
  /** Whether to skip the value */
  skip: boolean;
  /**
   * Whether to use the value as is.
   * If not skipping, whether to replace value
   */
  useValue?: boolean;
  /**
   * The exact value to use.
   * It will not be processed further (e.g., if you return an object, it won't be flattened)
   */
  value?: any;
}

/** Callback deciding per value whether to skip, replace, or keep flattening. */
export type Transformer = (value: any, opts: TransformerOptions) => TransformerResult;

/**
 * Recursively flattens an object into one level, where keys are separated by dot.
 * **WARNING**: Be careful not to use it on circular objects.
 * @param object nested object to flatten
 * @param transformer per-value hook to skip or replace values during flattening
 * @param _path Internal use: The path leading to the current value
 */
export function flattenObjectTransformer(object: object, transformer: Transformer, _path: string = ""): Record<string, any> {
  const flat: Record<string, any> = {};

  for (const [key,value] of Object.entries(object)) {
    if (_path) _path += `.${key}`;

    const result = transformer(value, {
      key,
      path: _path,
    });

    // Handlers if you wanted to transform...
    if (result.skip) continue;
    if (result.useValue) {
      flat[key] = result.value;
      continue;
    }
    // ... and fallthrough if not

    // If it's an object, flatten it recursively...
    if (_.isObject(value)) {
      const ref = flattenObjectTransformer(value, transformer, _path);

      for (const [k,v] of Object.entries(ref)) {
        const result = transformer(v, {
          key: k,
          path: `${key}.${k}`,
        });

        // Handlers if you wanted to transform...
        if (result.skip) continue;
        if (result.useValue) {
          flat[`${key}.${k}`] = result.value;
          continue;
        }
        // ... and fallthrough if not

        flat[`${key}.${k}`] = v;
      }

      continue;
    }

    // ... else it's some value
    flat[key] = value;
  }

  return flat;
}
